import React, { useEffect, useMemo, useState } from "react";
import { Alert, Container } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { publicApi } from "../../api/http";
import { authAxios, useAuth } from "../../contexts/AuthContext";
import {
  TOP_64_COMPETITION_NAME,
  TOP_64_COMPETITION_GIF_URL,
  TOP_64_CREATE_PATH,
  TOP_64_DEADLINE_LABEL,
  TOP_64_OFFICIAL_RANKING_ID,
  TOP_64_PLAYER_COUNT,
  TOP_64_PRIZE_USD,
  TOP_64_RULES_PATH,
  TOP_64_SCORING_TIERS,
  TOP_64_MAX_POINTS_PER_PLAYER,
} from "../../constants/top64Competition";
import { isRankingPublished } from "../../utils/independentRanking";
import "./Top64Competition.css";

const RULES = [
  {
    title: "Build your board",
    detail:
      "Rank any number of 2026 prospects (at least one, up to 64). Scoring is based on the first 64 picks of the draft.",
  },
  {
    title: "One entry per person",
    detail: "You may only submit a single competition ranking.",
  },
  {
    title: `Submit by ${TOP_64_DEADLINE_LABEL}`,
    detail: "After the deadline, competition entries cannot be edited or deleted.",
  },
  {
    title: "Entries stay hidden until release",
    detail:
      "Competition boards are saved as private drafts and are not visible on the site until after the entry deadline.",
  },
  {
    title: "Mark your ranking as a competition entry",
    detail: "Toggle the competition option when creating or editing your independent ranking.",
  },
  {
    title: "Drafts are allowed",
    detail:
      "Save and update your entry before the deadline. Competition boards stay private until after the deadline.",
  },
  {
    title: "Ties split the prize",
    detail: `If multiple entries tie for the highest score, the $${TOP_64_PRIZE_USD} USD grand prize is divided equally among all tied winners.`,
  },
];

function Top64Competition() {
  const { accessToken } = useAuth();
  const [officialRanking, setOfficialRanking] = useState(null);
  const [existingEntryId, setExistingEntryId] = useState(null);
  const [existingEntryPublished, setExistingEntryPublished] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    publicApi
      .get(`/official_rankings/${TOP_64_OFFICIAL_RANKING_ID}`)
      .then((res) => setOfficialRanking(res.data))
      .catch(() => setLoadError("Could not load competition details."));
  }, []);

  useEffect(() => {
    if (!accessToken) {
      setExistingEntryId(null);
      setExistingEntryPublished(false);
      return;
    }

    authAxios
      .get("/independent_rankings/mine")
      .then((res) => {
        const entry = (res.data || []).find(
          (row) => row.official_ranking_id === TOP_64_OFFICIAL_RANKING_ID
        );
        setExistingEntryId(entry?.independent_ranking_id ?? null);
        setExistingEntryPublished(isRankingPublished(entry));
      })
      .catch(() => {
        setExistingEntryId(null);
        setExistingEntryPublished(false);
      });
  }, [accessToken]);

  const deadlinePassed = useMemo(() => {
    if (!officialRanking?.deadline) return false;
    const deadline = new Date(officialRanking.deadline.replace(" ", "T") + "Z");
    return Date.now() > deadline.getTime();
  }, [officialRanking]);

  let ctaLabel = "Create your entry";
  let ctaTo = TOP_64_CREATE_PATH;

  if (deadlinePassed) {
    ctaLabel = "Deadline passed";
    ctaTo = null;
  } else if (!accessToken) {
    ctaLabel = "Sign in to join";
    ctaTo = `/login?redirect=${encodeURIComponent(TOP_64_RULES_PATH)}`;
  } else if (existingEntryId) {
    if (existingEntryPublished) {
      ctaLabel = "View your entry";
      ctaTo = `/independent-rankings/${existingEntryId}`;
    } else {
      ctaLabel = "Edit your entry";
      ctaTo = `/your-rankings/${existingEntryId}/edit`;
    }
  }

  return (
    <>
      <Helmet>
        <title>{TOP_64_COMPETITION_NAME} - Hockey Pipeline</title>
        <meta
          name="description"
          content={`Predict the 2026 NHL Draft. The most accurate board wins $${TOP_64_PRIZE_USD} USD.`}
        />
        <link rel="canonical" href={`https://hockeypipeline.com${TOP_64_RULES_PATH}`} />
      </Helmet>

      <Container className="top64-page" maxWidth="lg">
        {loadError && (
          <Alert severity="error" className="top64-alert">
            {loadError}
          </Alert>
        )}

        <header className="top64-hero">
          <div className="top64-hero-bg" aria-hidden="true">
            <img src={TOP_64_COMPETITION_GIF_URL} alt="" />
          </div>
          <div className="top64-hero-overlay" aria-hidden="true" />
          <div className="top64-hero-content">
            <div className="top64-hero-copy">
              <p className="top64-hero-eyebrow">2026 NHL Draft</p>
              <h1 className="top64-hero-title">Top 64 Challenge</h1>
              <p className="top64-hero-lead">
                Predict the first {TOP_64_PLAYER_COUNT} picks — closest board wins ${TOP_64_PRIZE_USD} USD.
              </p>
            </div>
            <div className="top64-hero-prize">
              <span className="top64-hero-prize-amount">${TOP_64_PRIZE_USD}</span>
              <span className="top64-hero-prize-label">Grand prize</span>
            </div>
          </div>
        </header>

        <div className="top64-layout">
          <div className="top64-main">
            <section className="top64-panel top64-panel--compact">
              <h2 className="top64-panel-title">How it works</h2>
              <ol className="top64-steps top64-steps--inline">
                <li>
                  <span className="top64-step-num">1</span>
                  <div>
                    <strong>Build your board</strong>
                    <p>Rank your top 64 prospects.</p>
                  </div>
                </li>
                <li>
                  <span className="top64-step-num">2</span>
                  <div>
                    <strong>Save your entry by the deadline</strong>
                    <p>{TOP_64_DEADLINE_LABEL}. Your board stays private until entries are released.</p>
                  </div>
                </li>
                <li>
                  <span className="top64-step-num">3</span>
                  <div>
                    <strong>Highest score wins</strong>
                    <p>
                      ${TOP_64_PRIZE_USD} USD to the top entry after the draft. If multiple
                      entries tie for first, the prize is split equally among the winners.
                    </p>
                  </div>
                </li>
              </ol>
            </section>

            <section className="top64-panel top64-panel--compact">
              <h2 className="top64-panel-title">How scoring works</h2>
              <p className="top64-scoring-intro">
                Each draft pick earns points by tier (max{" "}
                {(TOP_64_MAX_POINTS_PER_PLAYER * TOP_64_PLAYER_COUNT).toLocaleString()} total).
              </p>
              <ul className="top64-scoring-tiers">
                {TOP_64_SCORING_TIERS.map((tier) => (
                  <li key={tier.label} className="top64-scoring-tier">
                    <span className="top64-scoring-tier-label">{tier.label}</span>
                    <span className="top64-scoring-tier-points">{tier.points}</span>
                  </li>
                ))}
                <li className="top64-scoring-tier top64-scoring-tier--zero">
                  <span className="top64-scoring-tier-label">Not on your board</span>
                  <span className="top64-scoring-tier-points">0</span>
                </li>
              </ul>
            </section>

            <section className="top64-panel top64-panel--compact">
              <h2 className="top64-panel-title">Rules</h2>
              <ul className="top64-rules top64-rules--grid">
                {RULES.map((rule) => (
                  <li key={rule.title} className="top64-rule">
                    <span className="top64-rule-marker" aria-hidden="true" />
                    <div>
                      <strong>{rule.title}</strong>
                      <p>{rule.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <section className="top64-panel top64-panel--compact top64-panel--accent">
              <h2 className="top64-panel-title">Prize &amp; eligibility</h2>
              <div className="top64-prize-row">
                <div className="top64-prize-callout">
                  <span className="top64-prize-callout-amount">${TOP_64_PRIZE_USD} USD</span>
                  <p>
                    Highest total score after the 2026 NHL Draft. If there is a tie for
                    first place, the ${TOP_64_PRIZE_USD} USD prize is divided equally
                    among all tied winners.
                  </p>
                </div>
                <div className="top64-verification">
                  <h3 className="top64-verification-title">Identity verification</h3>
                  <p>
                    We verify your profile name matches the creator name on your entry.
                    If it does not match a valid government-issued ID, the prize goes to
                    the next eligible entrant.
                  </p>
                </div>
              </div>
            </section>
          </div>

          <aside className="top64-sidebar">
            <div className="top64-cta-panel">
              <p className="top64-cta-eyebrow">Ready to play?</p>
              <h2 className="top64-cta-title">Join the competition</h2>
              <p className="top64-cta-deadline">
                Entries close {TOP_64_DEADLINE_LABEL}
              </p>
              <div className="top64-cta-actions">
                {ctaTo ? (
                  <Link to={ctaTo} className="top64-cta-button">
                    {ctaLabel}
                  </Link>
                ) : (
                  <span className="top64-cta-button top64-cta-button--disabled">
                    {ctaLabel}
                  </span>
                )}
                <Link to="/independent-rankings" className="top64-cta-secondary">
                  Browse community rankings
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </Container>
    </>
  );
}

export default Top64Competition;
